import { inject, Injectable, Injector, Resource, ResourceRef, untracked } from "@angular/core";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { buffer, debounceTime, distinctUntilChanged, filter, from, map, mergeMap, Observable, shareReplay, Subject, takeUntil} from "rxjs";
import { APIStates, AppService } from "src/services/app.service";
import { SIGNAL, SignalNode } from '@angular/core/primitives/signals';
import { DateTime } from "luxon";
import { EventType, NotifyChannel, RPC } from "@vierkant-software/types__api";


export type DetailLevelType<TType extends Record<TDetail, object>, TDetail extends number> =
    TType extends Record<TDetail, object> ? TType[TDetail] : TType;

export interface SyncLoadedData<TType extends Record<number, object> & { [0]: object }, TDetail extends number> {
    ID: string;
    data?: DetailLevelType<TType, typeof this['detail']>;
    detail?: TDetail;
    eventTime?: DateTime;
}

export const EVICT = Symbol("evict");
export const RELOAD = Symbol("reload");
export const IGNORE = Symbol("ignore");
export const LOAD = Symbol("load");

export interface SyncDataChangeEvent<TDetail extends number> {
    type: 'loaded' | 'injected' | 'evicted';
    items: Array<{ ID: string, detail: TDetail }>;
}

interface ResourceRequest<TType extends Record<number, object> & { [0]: object }, TDetail extends number>
    extends SyncLoadedData<TType, TDetail>
{
    action: 'load' | 'evict' | 'push';
    force?: boolean;
}

interface SyncCacheInfo<TType extends Record<number, object> & { [0]: object }, TDetail extends number>
 extends SyncLoadedData<TType, TDetail> {
    resource: ResourceRef<DetailLevelType<TType, TDetail>>;
}

@Injectable()
export abstract class SyncServiceBase<
    TType extends Record<number, object> & { [0]: object},
    TDetail extends number & (0 extends TDetail ? unknown : never) = 0
> {
    protected app: AppService = inject(AppService);
    private _inject: Injector = inject(Injector);

    // these syncs run over the device session and keep running without a user
    protected get usesDeviceSession(): boolean {
        return false;
    }
    private readonly onDevice = this.usesDeviceSession;

    protected get syncApi(): RPC {
        return this.onDevice ? this.app.deviceApi : this.app.api;
    }

    protected cache: Map<string, SyncCacheInfo<TType, TDetail>> = new Map();
    protected lastValidTime = DateTime.fromMillis(0);

    private dataChangeSubject = new Subject<SyncDataChangeEvent<TDetail>>();
    public readonly dataChange$: Observable<SyncDataChangeEvent<TDetail>> = this.dataChangeSubject.asObservable();
    private dataChangeSignal = toSignal(this.dataChange$);

    private loadHandler = new Subject<ResourceRequest<TType, TDetail>>();
    private loadTrigger = this.loadHandler.pipe(debounceTime(1000));
    private loadPipe = this.loadHandler.pipe(
        buffer(this.loadTrigger),   //start 1s timer only when first event arrives
        mergeMap((requests) => {
            return from(this.processUpdateRequests(requests)).pipe(
                mergeMap((data) => {
                    return from(data); //convert array back to individual items
                })
            );
        }),
        shareReplay(1, 10)
    );
    private loadSubs = this.loadPipe.subscribe((x) => { /* console.log("Loaded data:", x); */ });

    private connStateSubs = this.app.$connectionStatus.pipe(
        filter((state) => this.onDevice ? !!this.app.syncSession : this.app.state === APIStates.loggedIn)
    ).subscribe((state) => {
        if (state === 'connected')
            this.doResync(false).catch(console.error);
    });

    // only a new device session reloads, a switch keeps the data
    private syncSessionSubs = this.onDevice
        ? this.app.$SyncSession.pipe(map(Boolean), distinctUntilChanged(), filter(Boolean)).subscribe(() => {
            this.doResync(false).catch(console.error);
        })
        : undefined;

    private stateSubs = this.app.$APIState.subscribe((state) => {
        if (this.onDevice)
            return;
        switch(state.type) {
            case APIStates.loggedIn:
                void this.doResync(true);
                break;
            case APIStates.loggedOut:
                if (this.onLogout === EVICT)
                    this.cleanup();
                break;
        }
    });

    private eventSubs = this.app.$events.subscribe((event) => {
        if (!this.onDevice && this.app.state !== APIStates.loggedIn)
            return;
        if (event.data.type !== EventType.sync || event.data.syncTarget !== this.syncName)
            return;
        // in the till the sync channel arrives over the user and the device session
        const viaDeviceSession = event.sessionId === this.app.syncSession;
        if (this.app.syncSession && viaDeviceSession !== this.onDevice)
            return;
        this.syncEvent(event.data);
    });

    protected syncEvent(data: { type: EventType.sync, syncTarget: string, ID: string, evict?: boolean }) {
        this.evict(data.ID);
    }

    private cleanup() {
        Array.from(this.cache.values()).forEach((c) => {
            if (c.resource)
                c.resource.destroy();
        });
        this.cache = new Map();
    }

    private canEvict(request: ResourceRequest<TType, TDetail>): boolean {
        const resource = this.cache.get(request.ID)?.resource;
        if (!resource)
            return true;
        const node = resource.value[SIGNAL] as SignalNode<DetailLevelType<TType, TDetail>>;
        return (node.consumers === undefined || node.consumers.consumer === undefined);
    }

    private doEvict(request: ResourceRequest<TType, TDetail>[]): void {
        for (let i = 0; i < request.length; i++) {
            const c = this.cache.get(request[i].ID);
            if (c) {
                this.cache.delete(request[i].ID);
                c.resource.destroy();
            }
        }
    }

    private async doResync(subscribe: boolean) {
        // console.log("DoResync", subscribe);
        if (await this.onInit(subscribe, this.cache.size === 0))
            return true;

        const recordsToResync = Array.from(this.cache.values()).reduce((acc, { ID, detail }) => {
            const ev = { ID, detail, action: 'evict' } as ResourceRequest<TType, TDetail>;
            if (this.canEvict(ev) && this.onEvict(ev) !== RELOAD)
                this.doEvict([ev]);
            else
                acc[ID] = detail;
            return acc;
        }, {} as Record<string, TDetail>);

        if (Object.keys(recordsToResync).length === 0)
            return;

        const result = await this.onResync(this.lastValidTime, recordsToResync);

        const resResult = result.map((x) => ({ ...x, action: 'push' } as ResourceRequest<TType, TDetail>));
        this.lastValidTime = resResult.reduce((acc, data) => {
            if (!DateTime.isDateTime(data.eventTime))
                throw new Error('eventTime is missing in resync Data for ' + data.ID + ' in ' + this.constructor.name);
            return DateTime.max(acc, data.eventTime);
        }, this.lastValidTime);

        resResult.forEach((data) => {
            this.loadHandler.next(data);
        });
    }

    private async processUpdateRequests(data: ResourceRequest<TType, TDetail>[]): Promise<SyncLoadedData<TType, TDetail>[]> {

        const push: Map<string, ResourceRequest<TType, TDetail>> =  new Map();
        const load: Map<string, ResourceRequest<TType, TDetail>> = new Map();
        const evict: Map<string, ResourceRequest<TType, TDetail>> =  new Map();

        for (let i = 0; i < data.length; i++) {
            switch(data[i].action) {
                case 'load':
                    //Load has Priority over push and evict
                    if (!load.has(data[i].ID) || load.get(data[i].ID).detail < data[i].detail)
                        load.set(data[i].ID, data[i]);
                    evict.delete(data[i].ID);
                    if (push.has(data[i].ID)) {
                        const pushData = push.get(data[i].ID);
                        //if push has higher detail, load the higher detail
                        if (pushData.detail >= data[i].detail)
                            data[i].detail = pushData.detail;
                        push.delete(data[i].ID);
                    }
                    break;
                case 'push':
                    if (load.has(data[i].ID)) {
                        const loadData = load.get(data[i].ID);
                        //load has priority over push
                        //higher detail value is kept
                        if (loadData.detail < data[i].detail)
                            loadData.detail = data[i].detail;
                    } else if (evict.has(data[i].ID)) {
                        // if push and evict are both present, transform into load
                        load.set(data[i].ID, data[i]);
                        evict.delete(data[i].ID);
                    } else {
                        push.set(data[i].ID, data[i]);
                    }
                    break;
                case 'evict':
                    //Reload non-evictable items
                    //convert push and evict to load if either is present
                    if (!data[i].force && (!this.canEvict(data[i]) || push.has(data[i].ID) || load.has(data[i].ID) || this.onEvict(data[i]) === RELOAD)) {
                        const p = push.get(data[i].ID);
                        const l = load.get(data[i].ID);
                        load.set(data[i].ID, { ...data[i],
                            detail: Math.max(p?.detail ?? 0, l?.detail ?? 0, data[i].detail ?? 0, this.cache.get(data[i].ID)?.detail ?? 0) as TDetail
                        });
                        push.delete(data[i].ID);
                        evict.delete(data[i].ID);
                    } else {
                        evict.set(data[i].ID, data[i]);
                    }
                    break;
            }
        }

        // console.log('Sync ', this.constructor.name, { load, push, evict });

        const evictArray = Array.from(evict.values());
        this.doEvict(evictArray);

        const request: Record<string, TDetail> = Array.from(load.values()).reduce((acc, req) => {
            if (acc[req.ID] === undefined || acc[req.ID] < req.detail)
                acc[req.ID] = req.detail;
            return acc;
        }, {} as Record<string, TDetail>);

        let result: SyncLoadedData<TType, TDetail>[] = [];
        if (Object.keys(request).length > 0)
            result = await this.load(request);
        this.lastValidTime = result.reduce((acc, req) => {
            if (!DateTime.isDateTime(req.eventTime))
                throw new Error('eventTime is missing in loaded Data for ' + req.ID + ' in ' + this.constructor.name);
            return DateTime.max(acc, req.eventTime);
        }, this.lastValidTime);
        result.push(...Array.from(push.values()));

        if (evictArray.length > 0) {
            this.dataChangeSubject.next({
                type:  'evicted',
                items: evictArray.map(r => ({ ID: r.ID, detail: r.detail }))
            });
        }

        const loadArray = Array.from(load.values());
        if (loadArray.length > 0) {
            this.dataChangeSubject.next({
                type:  'loaded',
                items: loadArray.map(r => ({ ID: r.ID, detail: r.detail }))
            });
        }

        const pushArray = Array.from(push.values());
        if (pushArray.length > 0) {
            this.dataChangeSubject.next({
                type:  'injected',
                items: pushArray.map(r => ({ ID: r.ID, detail: r.detail }))
            });
        }

        return result;
    }

    private _get(id: string, detail: TDetail, data?: DetailLevelType<TType, TDetail>): Resource<DetailLevelType<TType, TDetail>> {
        let cacheObj = this.cache.get(id);
        if (cacheObj) {
            if (cacheObj.detail < detail) {
                cacheObj.detail = detail;
                cacheObj.resource.reload();
            }
            return cacheObj.resource.asReadonly();
        }
        cacheObj = {
            ID:       id,
            data:     data,
            detail:   detail,
            resource: undefined,
        };
        this.cache.set(id, cacheObj);
        untracked(() => {
            cacheObj.resource = rxResource({
                defaultValue: this.getDefaultValue(id, detail),
                stream:       ({ abortSignal, previous }) => {
                    // console.log("LOADING", cacheObj.ID, "with detail", detail, "previous:", previous);
                    if (previous.status === 'idle' && cacheObj.data)
                        this.loadHandler.next({ action: 'push', ID: id, detail: cacheObj.detail, data: cacheObj.data} as ResourceRequest<TType, TDetail>);
                    else
                        this.loadHandler.next({ action: 'load', ID: id, detail: cacheObj.detail} as ResourceRequest<TType, TDetail>);
                    const canceled = new Subject<void>();
                    abortSignal.onabort = () => {
                        canceled.next();
                        canceled.complete();
                        console.log("ABORTED", cacheObj.ID);
                    };
                    return this.loadPipe.pipe(
                        takeUntil(canceled),
                        filter((data) => data.ID === id && data.detail >= cacheObj.detail),
                        map(data => data.data as DetailLevelType<TType, TDetail>),
                    );
                },
                injector: this._inject,
            });
        });
        return cacheObj.resource.asReadonly();
    }

    //#region abstract API
    /**
     * Fetch data from the backend for a number of resource requests.
     * @param data a map of ID to detail level of resources to load from the backend
     * @returns a list of loaded data items. Items missing will retain their default value.
     */
    protected abstract load(data: Record<string, TDetail>): Promise<SyncLoadedData<TType, TDetail>[]>;
    /**
     * This function returns a default value for each resource that can be used to display a placeholder
     * until the resource load is completed.
     * @param id the ID of the resource to get
     * @param detail detail level of the resource to get
     * @return a default value for the resource
     */
    protected abstract getDefaultValue(id: string, detail?: TDetail): DetailLevelType<TType, TDetail>;
    /**
     * is called to perform a resync of the data.
     * @param lastEvent the last successfull event time - everything after this needs to be reloaded
     * @param records the list of records that are retained in the cache and that need to be resynced from the backend
     * @returns a list of loaded data items. Items missing in the result set will remain unchanged in the cache.
     */
    protected abstract onResync(lastEvent: DateTime, records: Record<string, TDetail>): Promise<SyncLoadedData<TType, TDetail>[]>;
    /**
     * onInit is called the the service is initialied, as user logs in, or the websocket connection is reestablished
     * Perform needed channels subscriptions here via the this.app.subscribeChannel() method.
     *
     * @param subscribe if true, the service should subscribe to its channels
     * @param firstLoad if true, this is the initial load of the service (No data are present in the cache)
     * @returns true if a initial load was performed, false if a resync should be done
     */
    protected onInit(subscribe: boolean, firstLoad: boolean): Promise<boolean> | boolean {
        // return true if a initial load was done
        return false;
    }
    /**
     * onEvict is called for each resource item that is about to be evicted.
     *
     * @param data the load request that is about to be evicted
     * @returns EVICT to permit the eviction from the cache, RELOAD to prevent the recond from being evicted
     *          If RELOAD is returned, the item will be reloaded from the backend
     */
    protected onEvict(data: SyncLoadedData<TType, TDetail>): typeof EVICT | typeof RELOAD {
        return EVICT;
    }
    /**
     * if set to EVICT (the default), all data int he cache are evicted on logout
     * if set to IGNORE, the cache is retained on logout. Please note that no
     * channel subscriptions are retained, and a resync will be performed on the next login.
     */
    protected readonly onLogout: typeof EVICT | typeof IGNORE = EVICT;
    /**
     * syncName is the name of the sync service as used by the backend to identify this service.
     */
    protected readonly syncName: string;

    //#endregion

    // public API
    public get(id: string): Resource<DetailLevelType<TType, 0>>;
    public get<T extends TDetail>(id: string, detail: T): Resource<DetailLevelType<TType, T>>;
    get(id: string, detail?: TDetail): Resource<DetailLevelType<TType, TDetail>> | Resource<DetailLevelType<TType, 0>> {
        return this._get(id, detail ?? 0 as TDetail);
    }

    public inject(id: string, data: DetailLevelType<TType, 0>): void;
    public inject<T extends TDetail>(id: string, data: DetailLevelType<TType, T>, detail: T): void;
    public inject(data: Record<string, DetailLevelType<TType, 0>>): void;
    public inject<T extends TDetail>(data: Record<string, DetailLevelType<TType, T>>, detail: T): void;
    inject(id_or_data: string | Record<string, unknown>, data_or_detail?: unknown | number, det?: TDetail) {
        const detail: TDetail = typeof det != 'undefined' ? det : (data_or_detail as TDetail) ?? 0 as TDetail;
        const data: Record<string, DetailLevelType<TType, TDetail>> =
            typeof id_or_data === 'string' ?
                { [id_or_data]: data_or_detail as DetailLevelType<TType, TDetail> } :
                id_or_data as Record<string, DetailLevelType<TType, TDetail>>;

        Object.entries(data).forEach(([id, value]) => {
            const c = this.cache.get(id);
            if (!c) {
                this._get(id, detail, value);
                //inject only if data are not present, or detail level is higher
                //to prevent race conditions when simultanious reloading data from the backend
            } else if (c.detail < detail) {
                c.detail = detail;
                c.data = value;
                c.resource.set(value);
            }
        });
    }

    public async preload(ids: string | string[], detail: TDetail = 0 as TDetail): Promise<boolean> {
        if (!Array.isArray(ids))
            ids = [ids];
        ids = ids.filter(id => !this.cache.has(id) || this.cache.get(id).detail < detail);
        const completed = new Set<string>();
        const done = new Subject<void>();
        ids.forEach(id => {
            this._get(id, detail);
        });
        await this.loadPipe.pipe(
            filter((data) => ids.includes(data.ID) && data.detail >= detail),
            takeUntil(done),
        ).forEach((result) => {
            completed.add(result.ID);
            if (completed.size === ids.length) {
                done.next();
                done.complete();
            }
        });
        return true;
    }

    public evict(ids: string | string[], force: boolean = false): void {
        if (!Array.isArray(ids))
            ids = [ids];
        ids.forEach(id => {
            this.loadHandler.next({ ID: id, action: 'evict', force });
        });
    }

    get allIDs(): MapIterator<string> {
        this.dataChangeSignal();
        return this.cache.keys();
    }

    get allIDsCount(): number {
        this.dataChangeSignal();
        return this.cache.size;
    }

    // internal API
}

@Injectable()
export abstract class AutoSyncServiceBase<
    TType extends Record<number, object> & { [0]: object},
    TDetail extends number & (0 extends TDetail ? unknown : never) = 0
> extends SyncServiceBase<TType, TDetail> {
    /**
     * Maps the files to the corresponding data entries.
     * @param data The data entries to map the files to.
     * @param files The files to map.
     * @returns The mapped data entries.
     */
    protected mapFiles(data: SyncLoadedData<TType, TDetail>[], files: string[]): SyncLoadedData<TType, TDetail>[] {
        return data;
    }

    protected override async onInit(subscribe: boolean, firstLoad: boolean): Promise<boolean> {
        if (subscribe)
            await this.app.subscribeChannel(NotifyChannel.sync);
        return false;
    }

    protected override async load(request: Record<string, TDetail>): Promise<SyncLoadedData<TType, TDetail>[]> {
        const result = (await this.syncApi.FrontendSyncWorker.loadSyncData(this.syncName, request));
        if (result.__files.length > 0)
            return this.mapFiles(result.data as SyncLoadedData<TType, TDetail>[], result.__files);
        return result.data as SyncLoadedData<TType, TDetail>[];
    }

    protected override async onResync(lastEvent: DateTime, request: Record<string, TDetail>): Promise<SyncLoadedData<TType, TDetail>[]> {
        // console.log('Requesting reSync for ', this.syncName, lastEvent, request);
        const result = (await this.syncApi.FrontendSyncWorker.loadSyncData(this.syncName, request, lastEvent).catch((x) => { console.error(x); throw x; }));
        if (result.__files.length > 0)
            return this.mapFiles(result.data as SyncLoadedData<TType, TDetail>[], result.__files);
        return result.data as SyncLoadedData<TType, TDetail>[];
    }
}

@Injectable()
export abstract class FullSyncableServiceBase<
    TType extends Record<number, object> & { [0]: object},
    TDetail extends number & (0 extends TDetail ? unknown : never) = 0
> extends AutoSyncServiceBase<TType, TDetail> {
    private fullSyncMode = false;
    private fullSyncDetailLevel: TDetail;

    public async fullSync(detail: TDetail): Promise<void> {
        return await this.doFullSync(detail);
    }

    private async doFullSync(detail: TDetail, onlyAfter?: DateTime) {
        this.fullSyncMode = true;
        this.fullSyncDetailLevel = detail;
        const result = await this.syncApi.FrontendSyncWorker.loadSyncData(this.syncName, detail, onlyAfter);
        if (result.__files.length > 0)
            this.mapFiles(result.data as SyncLoadedData<TType, TDetail>[], result.__files);
        this.inject<TDetail>(Object.fromEntries(result.data.map((entry => [entry.ID, entry.data as DetailLevelType<TType, TDetail>]))), detail);
    }

    protected override async onInit(subscribe: boolean, firstLoad: boolean): Promise<boolean> {
        if (this.fullSyncMode)  {
            this.doFullSync(this.fullSyncDetailLevel, this.lastValidTime).catch(console.error);
            return true;
        }
        return super.onInit(subscribe, firstLoad);
    }

    protected override onEvict(data: SyncLoadedData<TType, TDetail>): typeof EVICT | typeof RELOAD {
        if (this.fullSyncMode)
            return RELOAD;
        return super.onEvict(data);
    }

    protected override syncEvent(data: { type: EventType.sync, syncTarget: string, ID: string, evict?: boolean }) {
        if (!data.evict && this.fullSyncMode)
            this.get(data.ID, this.fullSyncDetailLevel);
        this.evict(data.ID, data.evict);
    }
}
